import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'

import { loanProductQueryOptions, loanProductsQueryOptions } from '@/api/loans-api'
import { listGroups } from '@/api/groups-api'
import type { CustomerSummary } from '@/api/types'
import { PageHeader } from '@/components/page-header'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MemberPicker } from '@/features/customers/member-picker'
import { useBranchId } from '@/features/loans/use-business-day'
import { formatRate, INTEREST_PERIOD_LABELS } from '@/features/loans/constants'
import { LoanLink } from '@/features/loans/loan-link'
import { loanPaths, useLoanNavigate } from '@/features/loans/loan-paths'
import { useOpenLoanAccount } from '@/features/loans/mutations'
import {
  AppliedOutcome,
  FormField,
  OperationForm,
} from '@/features/loans/operation-form'
import { useLoanPermissions } from '@/features/loans/permissions'
import { LoanPrerequisitesPanel } from '@/features/loans/prerequisites-panel'
import { formatMoney } from '@/lib/money'

/**
 * Opening a loan account.
 *
 * The account is the file, not the lending: opening one commits nothing and lends nothing. What
 * it does do is fix the member, the product and the group for every round that will ever be
 * written on it, so all three are chosen deliberately and the gates are shown before the button.
 *
 * This screen does not refuse a narrow display. It keys no amount and moves no money — the gates
 * are checked against the product's smallest loan, which is what account opening asks about.
 */
export function LoanAccountNewPage() {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const branchId = useBranchId()
  const open = useOpenLoanAccount()

  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSummary | null>(null)
  const [productId, setProductId] = useState('')
  const [groupId, setGroupId] = useState('')
  const [openedAccountId, setOpenedAccountId] = useState<string | null>(null)

  const customerId = selectedCustomer?.id ?? ''

  const products = useQuery(loanProductsQueryOptions({ status: 'Active', pageSize: 100 }))
  const product = useQuery(loanProductQueryOptions(productId, permissions.canViewProducts))

  const groups = useQuery({
    queryKey: ['loans', 'group-picker', branchId] as const,
    queryFn: () => listGroups({ branchId: branchId ?? undefined, status: 'Active', pageSize: 100 }),
    enabled: product.data?.requiresGroup === true,
  })

  const requiresGroup = product.data?.requiresGroup ?? false
  const valid = customerId !== '' && productId !== '' && branchId !== null && (!requiresGroup || groupId !== '')

  return (
    <>
      <LoanLink
        to={loanPaths.accounts}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All loans
      </LoanLink>

      <PageHeader
        title="Open a loan account"
        description="The file a member's rounds are written in. Opening it lends nothing and promises nothing."
      />

      <OperationForm
        title="The member, the product and the group"
        description="All three are fixed for the life of the account. A different product is a different account."
        submitLabel="Open the account"
        isPending={open.isPending}
        disabled={!valid}
        error={open.isError ? open.error : null}
        onDone={() =>
          navigate(openedAccountId ? loanPaths.account(openedAccountId) : loanPaths.accounts)
        }
        outcome={
          openedAccountId ? (
            <AppliedOutcome title="Account opened">
              The account exists and has no rounds on it. Nothing has been lent — the first round
              starts with an application.
            </AppliedOutcome>
          ) : null
        }
        onSubmit={() => {
          if (!valid || branchId === null) {
            return
          }

          open.mutate(
            {
              customerId,
              productId,
              groupId: requiresGroup ? groupId : null,
              branchId,
            },
            { onSuccess: (account) => setOpenedAccountId(account.id) },
          )
        }}
        footnote="One account per member per product. A second on the same product is refused — loans.account_exists_for_product."
      >
        {branchId === null ? (
          <Alert tone="warning">
            <AlertTitle>No branch is selected</AlertTitle>
            <AlertDescription>
              An account belongs to a branch. Choose one in the header before opening it.
            </AlertDescription>
          </Alert>
        ) : null}

        <MemberPicker
          id="open-customer-search"
          label="Find the member"
          selected={selectedCustomer}
          onSelect={setSelectedCustomer}
          branchId={branchId}
          status="Active"
          kycStatus="Approved"
          helpText="Two characters or more. Searches the branch you are working in, by name or customer number — only approved customers can hold an account."
        />

        <FormField
          id="open-product"
          label="Product"
          required
          hint="Only active products take new applications. A retired one cannot be chosen."
        >
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger id="open-product">
              <SelectValue placeholder="Choose a product" />
            </SelectTrigger>
            <SelectContent>
              {(products.data?.items ?? []).map((row) => (
                <SelectItem key={row.id} value={row.id}>
                  {row.name} — {formatMoney(row.minPrincipalMinor, { currency: row.currencyCode })}{' '}
                  to {formatMoney(row.maxPrincipalMinor, { currency: row.currencyCode })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        {product.isSuccess ? (
          <Alert tone="info">
            <AlertTitle>
              {product.data.name} at {formatRate(product.data.interestRate)}{' '}
              {INTEREST_PERIOD_LABELS[product.data.interestRatePeriod]}
            </AlertTitle>
            <AlertDescription>
              Security deposit {formatRate(product.data.securityDepositPercent, 0)} of principal.
              {product.data.requiresGroup
                ? ' This product lends only through a group.'
                : ' Individual lending.'}
            </AlertDescription>
          </Alert>
        ) : null}

        {requiresGroup ? (
          <FormField
            id="open-group"
            label="Group"
            required
            hint="Active groups at this branch. A further round is refused where the group's health does not qualify it."
          >
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger id="open-group">
                <SelectValue placeholder="Choose a group" />
              </SelectTrigger>
              <SelectContent>
                {(groups.data?.items ?? []).map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name} — {group.groupCode}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {groups.isSuccess && groups.data.items.length === 0 ? (
              // An empty dropdown reads as broken — and as muted text this explanation was read
              // as exactly that during UAT. It is now the loudest thing on the field, with the
              // door to walk through, because a refusal must say where it is cleared.
              <Alert tone="warning" className="mt-2">
                <AlertTitle>No Active group at this branch — the dropdown is empty, not broken</AlertTitle>
                <AlertDescription>
                  A group is created Forming and only an Active group can borrow. Once its leader
                  is appointed, open the group&rsquo;s own record and use{' '}
                  <strong>Activate the group</strong>; it then appears here.{' '}
                  <LoanLink to="/groups" className="font-medium underline underline-offset-2">
                    Go to the groups list →
                  </LoanLink>
                </AlertDescription>
              </Alert>
            ) : null}
            {groups.isError ? (
              <Alert tone="warning" className="mt-2">
                <AlertTitle>The group list could not be read</AlertTitle>
                <AlertDescription>
                  Nothing is wrong with your selection — the groups lookup itself failed. Retry, or
                  report the reference from the browser console if it persists.
                </AlertDescription>
              </Alert>
            ) : null}
          </FormField>
        ) : null}

        {customerId !== '' && productId !== '' ? (
          <LoanPrerequisitesPanel
            customerId={customerId}
            productId={productId}
            title="Gates at the product's smallest loan"
          />
        ) : null}
      </OperationForm>
    </>
  )
}
